import React, { useState } from 'react';
import { Clock, Target, ChevronRight, ChevronLeft, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CATInstructions = ({ testInfo, onStartTest, userProfile }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isDeclarationChecked, setIsDeclarationChecked] = useState(false);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeHours = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const userName = userProfile?.name || userProfile?.displayName || 'Candidate';

  // Page 1: General Instructions
  const page1Content = (
    <div className="space-y-4 text-gray-700">
      <div className="space-y-3">
        <div className="flex items-start">
          <span className="font-semibold mr-2">1.</span>
          <div>
            <p className="mb-2">
              The test has <strong>3 (three) sections</strong>. The total duration of the test is <strong>120 minutes</strong>.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <span className="font-semibold mr-2">2.</span>
          <div>
            <p className="mb-2">
              The time allotted to each section is <strong>40 minutes</strong>. As soon as you start answering a section the clock (displayed on the top right corner of the screen) will start. On completion of 40 minutes, the clock will stop, the particular section will be locked and the responses to section will be auto-submitted. You will then need to move to the next section and start answering the next set of questions. The same process will be repeated for all three sections. On submission of all three sections a summary of your answers will be displayed on your screen.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <span className="font-semibold mr-2">3.</span>
          <div>
            <p>
              You would be allowed to leave the test hall only after minimum of <strong>120 minutes</strong>.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <span className="font-semibold mr-2">4.</span>
          <div>
            <p>
              The question paper will have a mix of <strong>Multiple-Choice Question (MCQ)</strong> type with options and <strong>non-MCQ</strong> type.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <span className="font-semibold mr-2">5.</span>
          <div>
            <p>
              Your time will be set and synced to the server clock. The <strong>Countdown Timer</strong> at the top right corner of your screen will display the remaining time available to you to complete the currently active section. When the timer reaches zero, the test for that section will automatically end.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <span className="font-semibold mr-2">6.</span>
          <div>
            <p>
              For rough work, you will be allowed to use the writing pad and pen provided to you. Please note that only one writing pad will be provided to you. Write your name and registration number clearly at the designated place on the writing pad. Return the writing pad with all pages intact and pen to the invigilator at the end of the test.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <span className="font-semibold mr-2">7.</span>
          <div>
            <p>
              You will not be allowed to use any calculator or any other computing machine or device. An <strong>on-screen calculator</strong> will be provided, which can be used for computing.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <span className="font-semibold mr-2">8.</span>
          <div>
            <p>
              The question palette displayed on the right side of screen will show the status of each question using one of the following symbols:
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Page 2: Question Status, Navigation Instructions, Other Important Instructions, and Declaration
  const page2Content = (
    <div className="space-y-6 text-gray-700 pb-8">
      {/* Question Status Table */}
      <div className="mb-4">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">S. No.</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Question with Status</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Meaning</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold">A</td>
                <td className="border border-gray-300 px-4 py-2">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-200 border border-gray-400 rounded text-gray-700 font-semibold">
                    1
                  </div>
                </td>
                <td className="border border-gray-300 px-4 py-2">You have not visited the question yet.</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold">B</td>
                <td className="border border-gray-300 px-4 py-2">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-red-100 border-2 border-red-500 rounded-full">
                    <span className="text-red-700 font-semibold">3</span>
                  </div>
                </td>
                <td className="border border-gray-300 px-4 py-2">You have visited the question but not answered it.</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold">C</td>
                <td className="border border-gray-300 px-4 py-2">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 border-2 border-green-600 rounded-full">
                    <span className="text-green-700 font-semibold">5</span>
                  </div>
                </td>
                <td className="border border-gray-300 px-4 py-2">You have answered the question but have not flagged it as Marked for Review.</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold">D</td>
                <td className="border border-gray-300 px-4 py-2">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 border-2 border-purple-600 rounded-full">
                    <span className="text-purple-700 font-semibold">7</span>
                  </div>
                </td>
                <td className="border border-gray-300 px-4 py-2">You have not answered the question but have flagged it as Marked for Review.</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-semibold">E</td>
                <td className="border border-gray-300 px-4 py-2">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 border-2 border-purple-600 rounded-full relative">
                    <span className="text-purple-700 font-semibold">9</span>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded flex items-center justify-center">
                      <span className="text-white text-xs font-bold">0</span>
                    </div>
                  </div>
                </td>
                <td className="border border-gray-300 px-4 py-2">You have answered the question as well as flagged it as Marked for Review.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400">
        <p className="text-sm text-gray-700">
          <strong>*</strong> If a question is <strong>answered</strong> and flagged as '<strong>Marked for Review</strong>' (Serial No. E) at the end of the test for that section, then the answer will be considered for evaluation.
        </p>
      </div>

      {/* Point 9 */}
      <div className="mt-4">
        <p className="mb-2">
          <span className="font-semibold">9.</span> You can click on the "<strong>&lt;</strong>" arrow which appears to the left of question palette to collapse the question palette and maximize the window. To view the question palette again, you can click on "<strong>&gt;</strong>" which appears on the right side of computer console.
        </p>
      </div>

      {/* Point 10 */}
      <div className="mt-6">
        <p className="font-semibold mb-3">
          <span className="font-semibold">10.</span> <strong>To answer a question, you will need to do the following:</strong>
        </p>
        <div className="ml-6 space-y-2">
          <p>
            <strong>a.</strong> Click on the question number in the question palette to go to that question directly.
          </p>
          <p>
            <strong>b.</strong> Select an answer for a Multiple Choice type question by clicking on the radio button () placed just before the choice. For Non-MCQ type questions, enter in the answer in the space provided on the screen using the on-screen keyboard.
          </p>
          <p>
            <strong>c.</strong> Click on '<strong>Save & Next</strong>' to save your answer for the current question and then go to the next question.
          </p>
        </div>
      </div>

      <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400">
        <p className="text-sm font-semibold text-red-800 mb-2">Caution:</p>
        <p className="text-sm text-red-700">
          Answers will <em>not</em> be saved if a user navigates directly to another question by clicking its number without first clicking "Save & Next" or "Mark for Review & Next".
        </p>
      </div>

      <div className="mt-4">
        <p className="mb-2">
          <span className="font-semibold">d.</span> You can view all questions of a section by clicking the "<strong>Question Paper</strong>" button, which displays the entire question paper section-wise.
        </p>
      </div>

      {/* Point 11 */}
      <div className="mt-6">
        <p className="font-semibold mb-3">
          <span className="font-semibold">11.</span> <strong>Procedure for changing your response to a question:</strong>
        </p>
        <div className="ml-6 space-y-2">
          <p>
            <strong>a.</strong> To deselect a chosen answer, click the question number on the palette and then the '<strong>Clear Response</strong>' button.
          </p>
          <p>
            <strong>b.</strong> To change an answer, click the radio button of another option.
          </p>
          <p>
            <strong>c.</strong> To save a changed answer, click '<strong>Save & Next</strong>' or '<strong>Mark for Review & Next</strong>'.
          </p>
        </div>
      </div>

      {/* Point 12 */}
      <div className="mt-6">
        <p className="font-semibold mb-3">
          <span className="font-semibold">12.</span> <strong>Navigating through Sections:</strong>
        </p>
        <div className="ml-6 space-y-3">
          <p>
            <strong>a.</strong> The test has three sections: <strong>I.</strong> Verbal Ability and Reading Comprehension (VARC), <strong>II.</strong> Data Interpretation and Logical Reasoning (DILR), and <strong>III.</strong> Quantitative Ability (QA). They are administered in order, displayed on the top bar, with the current section highlighted.
          </p>
          <p>
            <strong>b.</strong> Moving to the next section is only possible after completing a minimum of <strong>40 minutes</strong> in the current section.
          </p>
        </div>
      </div>

      <div className="mt-6">
        <p className="mb-2">
          Clicking on "<strong>Mark for Review & Next</strong>" saves the current answer, marks it for review, and moves to the next question. This review can be done anytime before the section is completed.
        </p>
      </div>

      {/* Other Important Instructions */}
      <div className="mt-8 pt-6 border-t border-gray-300">
        <h4 className="font-semibold mb-4">Other Important Instructions for Candidate:</h4>
        <div className="space-y-3">
          <div className="flex items-start">
            <span className="font-semibold mr-2">1.</span>
            <div>
              <p>
                Login: Enter your registration number and password as per the instructions given by the invigilator.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <span className="font-semibold mr-2">2.</span>
            <div>
              <p>
                Symbols: Please understand the symbols used in the test before starting.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <span className="font-semibold mr-2">3.</span>
            <div>
              <p className="mb-2">The paper has <strong>3 (three) sections</strong>:</p>
              <div className="ml-4 overflow-x-auto">
                <table className="border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Section</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Test</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2"><strong>I</strong></td>
                      <td className="border border-gray-300 px-4 py-2">Verbal Ability and Reading Comprehension (VARC)</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2"><strong>II</strong></td>
                      <td className="border border-gray-300 px-4 py-2">Data Interpretation and Logical Reasoning (DILR)</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2"><strong>III</strong></td>
                      <td className="border border-gray-300 px-4 py-2">Quantitative Ability (QA)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <span className="font-semibold mr-2">4.</span>
            <div>
              <p>
                For Data Interpretation and Logical Reasoning (DILR), each situation/scenario may consist of multiple questions. Similarly, for Reading Comprehension, each passage may consist of multiple questions.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <span className="font-semibold mr-2">5.</span>
            <div>
              <p>
                For <strong>MCQ-type questions</strong>, you get <strong>3 (three) marks</strong> for each correct answer, <strong>-1 (minus one) mark</strong> for each wrong answer, and <strong>0 (zero) marks</strong> for each un-attempted question.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <span className="font-semibold mr-2">6.</span>
            <div>
              <p>
                For <strong>Non-MCQ type questions</strong>, you get <strong>3 (three) marks</strong> for each correct answer, and <strong>0 (zero) marks</strong> for each wrong and un-attempted question. There will be <strong>no negative mark</strong> for non-MCQ type questions.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <span className="font-semibold mr-2">7.</span>
            <div>
              <p className="mb-2">
                <strong>MCQ:</strong> Choices, only one correct. Select by clicking a radio button placed before the option.
              </p>
              <p>
                <strong>Non-MCQ:</strong> Type the answer in the provided space using the on-screen keyboard.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <span className="font-semibold mr-2">8.</span>
            <div>
              <p>
                Your answers will be updated and saved periodically on the server. The test duration is <strong>120 minutes</strong>, after which it will end automatically. Section-wise time is <strong>40 minutes</strong> each, and you cannot go back to previous sections once a section's time is over.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Declaration */}
      <div className="mt-8 pt-6 border-t border-gray-300">
        <h4 className="font-semibold mb-4">Declaration by Candidate:</h4>
        <div className="space-y-3 text-sm">
          <p>
            I have read and understood all the above instructions. I confirm that I have read and understood the instructions on the admit card. I understand that my candidature is liable to be cancelled for violating any of the instructions. I confirm that the allotted computer hardware is in proper working condition at the start of the test.
          </p>
          <p>
            I agree to the Non-Disclosure Agreement. I will not disclose, publish, reproduce, transmit, store, or facilitate transmission/storage of the contents of the CAT exam in any form (verbal, written, electronic, mechanical). Violation is against the Indian Contract Act, 1872, Copyright Act, 1957, and Information Technology Act, 2000. Such actions constitute a cognizable offense punishable with imprisonment up to three years and a fine up to Rs. Two Lakhs. I agree to this Non-Disclosure Agreement.
          </p>
          <p>
            I declare that I am not in possession of / not wearing / not carrying any prohibited gadget like mobile phone, bluetooth devices etc. /any prohibited material with me into the Examination Hall.
          </p>
          <p>
            I agree that in case of not adhering to the instructions, I shall be liable to be debarred from this Test and/or to disciplinary action, which may include ban from future Tests / Examinations.
          </p>
        </div>

        <div className="mt-6 flex items-start">
          <input
            type="checkbox"
            id="declaration"
            checked={isDeclarationChecked}
            onChange={(e) => setIsDeclarationChecked(e.target.checked)}
            className="mt-1 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="declaration" className="ml-3 text-sm text-gray-700 cursor-pointer">
            I have read and understood the instructions. All computer hardware allotted to me are in proper working condition. I declare that I am not in possession of / not wearing / not carrying any prohibited gadget like mobile phone, bluetooth devices etc. /any prohibited material with me into the Examination Hall. I agree that in case of not adhering to the instructions, I shall be liable to be debarred from this Test and/or to disciplinary action, which may include ban from future Tests / Examinations.
          </label>
        </div>
      </div>
    </div>
  );

  const pages = [
    { title: 'General Instructions', content: page1Content },
    { title: 'Question Status & Other Instructions', content: page2Content }
  ];

  const nextPage = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleStartTest = () => {
    if (isDeclarationChecked) {
      onStartTest();
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-blue-600 h-16 flex items-center px-6 relative">
        <h1 className="text-2xl font-semibold text-white">Instructions</h1>
        {/* Light blue section extending down on the right */}
        <div className="absolute right-0 top-0 bottom-0 w-64 bg-blue-100"></div>
      </div>

      <div className="flex h-[calc(100vh-4rem)]">
        {/* Main Content Area - Scrollable */}
        <div className="flex-1 flex flex-col bg-white">
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="max-w-4xl mx-auto">
              {currentPage === 0 && (
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  General Instructions for Candidate:
                </h2>
              )}

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentPage}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {pages[currentPage].content}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Navigation Buttons - Fixed at bottom */}
          <div className="flex justify-between items-center px-8 py-4 border-t border-gray-200 bg-white">
            <button
              onClick={prevPage}
              disabled={currentPage === 0}
              className={`flex items-center px-4 py-2 rounded ${
                currentPage === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>

            {currentPage === pages.length - 1 ? (
              <button
                onClick={handleStartTest}
                disabled={!isDeclarationChecked}
                className={`flex items-center px-6 py-2 rounded font-medium ${
                  isDeclarationChecked
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                I am ready to begin
              </button>
            ) : (
              <button
                onClick={nextPage}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            )}
          </div>
        </div>

        {/* Right Sidebar - User Profile */}
        <div className="w-64 bg-blue-50 flex flex-col items-center py-8 px-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4 relative">
            <div className="absolute inset-0 bg-yellow-200 rounded-full opacity-50"></div>
            <User className="h-12 w-12 text-gray-600 relative z-10" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">{userName}</h3>
        </div>
      </div>
    </div>
  );
};

export default CATInstructions;

